import isPlainObject from 'lodash/isPlainObject';

// https://www.notion.so/sure/Personal-Identifiable-Information-PII-862bf3c86c7c4af98be18d9160c24b41
const defaultRedactedKeyPaths = [
  'address.line1',
  'address.line2',
  'dob',
  'driving_license_number',
  'email',
  'full_name',
  'last_name',
  'latitude',
  'longitude',
  'password',
  'request_ip',
  'ssn',
  'vin_services_order_date',
  'vin',
];

const redactedMessage = '**REDACTED**';

function parseJson(value: unknown): Record<string, unknown> | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

/**
 * Recursively iterates over an object or array deeply and sets the value of any
 * provided key paths as redacted, so that personal identifiable information
 * (PII) isn't sent to third party services such as Sentry, LogRocket, etc.
 *
 * @param userData Data that needs to be sanitized. Can be an object, array of
 * objects, or a scalar value.
 * @param redactedKeyPaths An array of key paths to mark as redacted, if found.
 * For example, 'foo.bar' would match { foo: { bar: '123' } }. Array indexes are
 * not included, since it's assumed that sensitive data should apply to all items
 * in an array instead of only items at certain positions within the array.
 *
 * Using an object like this:
 *
 * ```js
 * const body = {
 *   users: [
 *     { name: 'Bill', vin: 123 },
 *     { name: 'Bob', vin: 456 }
 *   ],
 * };
 *
 * In order to redact `vin` from all users, the key path would be `users.vin`.
 * `users[0].vin` is unsupported.
 * ```
 * @returns A sanitized value.
 *
 * Example
 * ```js
 * const safe = sanitize(
 *   { address: { line1: '123 Foo St', postal: 12345 } },
 *   ['address.line1']
 *); // { address: { line1: 'REDACTED', postal: 12345 }}
 * ```
 */
export function sanitize<T = unknown>(
  userData: T,
  redactedKeyPaths = defaultRedactedKeyPaths
): T {
  function redactImplementation(
    currentValue: any,
    visitedParentKeys: string[] = []
  ): any {
    if (Array.isArray(currentValue)) {
      return currentValue.map((value) =>
        redactImplementation(value, visitedParentKeys)
      );
    }

    if (isPlainObject(currentValue)) {
      const clone = { ...currentValue };

      Object.keys(clone).forEach((key) => {
        const value = clone[key];
        const visitedKeys = [...visitedParentKeys, key];
        const visitedKeyPath = visitedKeys.join('.');

        if (
          redactedKeyPaths.some((keyPath) => visitedKeyPath.endsWith(keyPath))
        ) {
          clone[key] = redactedMessage;
        } else {
          clone[key] = redactImplementation(value, visitedKeys);
        }
      });
      return clone;
    }

    // Handle cases where request or response bodies submit JSON as strings
    const json = parseJson(currentValue);
    if (json) {
      const sanitizedJson = redactImplementation(json, visitedParentKeys);
      return JSON.stringify(sanitizedJson);
    }

    return currentValue;
  }

  return redactImplementation(userData);
}

/**
 * Removes all sensitive data from URLs.
 *
 * Example
 * ```js
 * const safeUrl = sanitize('https://foo.com?id_token=abc123');
 * // safeUrl === 'https://foo.com?id_token=**REDACTED**'
 * ```
 */
export function sanitizeURL(url: string) {
  return url
    .replace(/access_token=([^&]*)/, `access_token=${redactedMessage}`)
    .replace(/id_token=([^&]*)/, `id_token=${redactedMessage}`);
}
